import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FlatListProps } from 'react-native';
import { Flex, GoodsCard, List, useScreenSize } from '@banhala/ui-kit';
import { isGoodsCard, type CardEntity } from './types';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { bridge, useNavigator } from '../../../../utils';
import { useAddLikeGoods, type useLikeMap } from '../../../../hooks';
import { useLogOnGoodsPressItem } from '../TwoColGoodsList/log/logOnGoodsPressItem';
import { useLogOnPressLike } from '../TwoColGoodsList/log/logOnPressLike';
import { useRemoveLikeGoods } from '../../../../queries';
import {
  cardRenderItemGenerator,
  type CardRenderItem,
} from './cardRenderItemGenerator';
import { PrimaryCard } from './PrimaryCard';
import { VideoGoodsCard, FullVideoGoodsCard } from './VideoGoodsCard';
import { useLogDisplayCardItems } from './log/useLogDisplayCardItems';

const NUM_COLUMNS = 2;
const COLUMN_GAP = 2;
const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

interface TwoColCardListProps extends ReturnType<typeof useLikeMap> {
  entity: CardEntity[];
}

export const TwoColCardList = ({
  entity,
  likeMap,
  setLikeMap,
}: TwoColCardListProps) => {
  const { navigator } = useNavigator();
  const logOnPressLike = useLogOnPressLike();
  const logOnGoodsPressItem = useLogOnGoodsPressItem();
  const { width: screenWidth } = useScreenSize();

  const itemWidth = useMemo(
    () => (screenWidth - COLUMN_GAP * (NUM_COLUMNS - 1)) / NUM_COLUMNS,
    [screenWidth],
  );

  const goodsEntities = useMemo(() => entity.filter(isGoodsCard), [entity]);

  // 비디오 카드 엔티티 (좋아요 초기화용)
  const videoEntities = useMemo(
    () => entity.filter(
      (e): e is import('./types').VideoGoodsCardEntity | import('./types').FullVideoGoodsCardEntity =>
        e.type === 'VIDEO_GOODS_CARD' || e.type === 'FULL_VIDEO_GOODS_CARD',
    ),
    [entity],
  );

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...goodsEntities.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
        ...videoEntities.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [goodsEntities, videoEntities, setLikeMap]);

  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { addLikeGoods } = useAddLikeGoods({
    listParams: goodsEntities[0]?.logging.analytics?.LIST_ID as
      | string
      | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      (goodsEntities[0]?.logging.analytics?.SCREEN as string | undefined) ?? '',
  });

  const listData = useMemo(() => cardRenderItemGenerator(entity), [entity]);

  const handlePressItem = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    logOnGoodsPressItem({ item, logging });
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      remoteDeeplink: item.remote_deeplink ?? undefined,
      loggingAnalytics: logging.analytics,
    });
  };

  const handlePressLike = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ item, logging, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  const { logDisplayCardItems } = useLogDisplayCardItems();

  // 비디오 카드 가시성 추적 — 화면에 보이는 비디오만 재생
  const [visibleVideoSnoSet, setVisibleVideoSnoSet] = useState<Set<number>>(new Set());
  const handleViewableItemsChanged = useCallback<
    NonNullable<FlatListProps<CardRenderItem>['onViewableItemsChanged']>
  >(
    (info) => {
      // 로깅 처리
      logDisplayCardItems(info);
      // 비디오 가시성 갱신
      const visibleSnoSet = new Set<number>();
      info.viewableItems.forEach(({ item, isViewable }) => {
        if (
          isViewable &&
          (item.type === 'VIDEO_GOODS_CARD' || item.type === 'FULL_VIDEO_GOODS_CARD')
        ) {
          visibleSnoSet.add(item.item.sno);
        }
      });
      setVisibleVideoSnoSet(visibleSnoSet);
    },
    [logDisplayCardItems],
  );

  const handlePressVideoItem = (item: CardRenderItem) => {
    if (item.type !== 'VIDEO_GOODS_CARD' && item.type !== 'FULL_VIDEO_GOODS_CARD') return;
    bridge.logAnalyticsEvent({
      name: 'CLICK_GOODS',
      analytics: { CARD_TYPE: 'VIDEO', ...item.logging.analytics },
      ably_only_log: item.logging.ably_only_log,
    });
    if (item.item.ad) {
      bridge.logAdEvent({ action: 'click' as const, ad: item.item.ad });
    }
    navigator.moveToGoods({
      sno: item.item.sno,
      firstPageRendering: item.item.first_page_rendering ?? null,
      loggingAnalytics: item.logging.analytics,
    });
  };

  const handlePressVideoLike = (item: CardRenderItem) => {
    if (item.type !== 'VIDEO_GOODS_CARD' && item.type !== 'FULL_VIDEO_GOODS_CARD') return;
    const currentLike = likeMap[item.item.sno] ?? false;
    bridge.logAnalyticsEvent({
      name: currentLike ? 'CLICK_UNLIKE_GOODS' : 'CLICK_LIKE_GOODS',
      analytics: { CARD_TYPE: 'VIDEO', ...item.logging.analytics },
      ably_only_log: item.logging.ably_only_log,
    });
    if (item.item.ad) {
      bridge.logAdEvent({
        action: currentLike ? ('unlike' as const) : ('like' as const),
        ad: item.item.ad,
      });
    }
    if (currentLike) {
      mutateRemoveLikeGoods({
        sno: item.item.sno,
        list_params_referrer: item.item.like?.list_id,
      });
      bridge.likeGoods({ goods_sno: item.item.sno, is_liked: false });
      setLikeMap(prev => ({ ...prev, [item.item.sno]: false }));
      return;
    }
    addLikeGoods(item.item.sno, item.logging.analytics, item.logging.ably_only_log);
  };

  const renderItem = ({
    item,
    index,
  }: {
    item: CardRenderItem;
    index: number;
  }) => {
    if (item.type === 'PRIMARY_CARD') {
      return (
        <Flex width={itemWidth}>
          <PrimaryCard
            item={item.item}
            logging={item.logging}
            width={itemWidth}
          />
        </Flex>
      );
    }
    if (item.type === 'VIDEO_GOODS_CARD') {
      return (
        <Flex width={itemWidth}>
          <VideoGoodsCard
            item={item.item}
            width={itemWidth}
            isLike={likeMap[item.item.sno] ?? false}
            isVisible={visibleVideoSnoSet.has(item.item.sno)}
            onPress={() => handlePressVideoItem(item)}
            onPressLike={() => handlePressVideoLike(item)}
          />
        </Flex>
      );
    }
    if (item.type === 'FULL_VIDEO_GOODS_CARD') {
      return (
        <Flex width={itemWidth}>
          <FullVideoGoodsCard
            item={item.item}
            width={itemWidth}
            isLike={likeMap[item.item.sno] ?? false}
            isVisible={visibleVideoSnoSet.has(item.item.sno)}
            onPress={() => handlePressVideoItem(item)}
            onPressLike={() => handlePressVideoLike(item)}
          />
        </Flex>
      );
    }
    return (
      <Flex width={itemWidth}>
        <GoodsCard
          data={item}
          size="medium"
          testID={`goods/${item.sno}?LIST_ID=${item.logging.analytics?.LIST_ID}&LIST_INDEX=${index}&DELIVERY_TYPE=${item.deliveryType}`}
          defaultWidth={itemWidth}
          onPress={() => handlePressItem(item)}
          onPressLike={async () => handlePressLike(item)}
          isLike={likeMap[item.sno]}
        />
      </Flex>
    );
  };

  return (
    <List<CardRenderItem>
      data={listData}
      numColumns={NUM_COLUMNS}
      columnWrapperStyle={{ gap: COLUMN_GAP }}
      onViewableItemsChanged={handleViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
      keyExtractor={(item, index) =>
        item.type === 'PRIMARY_CARD'
          ? `primary_${index}`
          : item.type === 'VIDEO_GOODS_CARD' || item.type === 'FULL_VIDEO_GOODS_CARD'
            ? `video_${item.item.sno}_${index}`
            : (item.sno ?? index).toString()
      }
      renderItem={renderItem}
    />
  );
};
